import { Playlist, ResourcePermission } from '@app/app/shared/models';
import { SidebarItem } from '@app/app/shared/models/sidebar/sidebar.model';

/** The management page of a course, programme or collection where its registration requests are decided. */
export const REGISTRATIONS_URI = 'registrations';

/** Whether the person can open the content's management pages for anything other than its registration requests. */
export function managesContent(permission: ResourcePermission | undefined): boolean {
  if (!permission) {
    return false;
  }
  return (
    permission.canEdit ||
    permission.canEditBadge ||
    permission.canEditGoal ||
    permission.canPublish ||
    permission.canViewAnalytics ||
    permission.canManageCollaboration ||
    permission.canManageTargeting ||
    (permission.reviewerDashboardPermission?.accessForGroups?.length ?? 0) > 0
  );
}

/** The backend counts the requests only for the people who decide on them. */
export function decidesRegistrations(content: Pick<Playlist, 'pendingRegistrations'> | undefined): boolean {
  return content?.pendingRegistrations !== undefined && content?.pendingRegistrations !== null;
}

/** Whether the URL is the registration requests page of a piece of content. */
export function isRegistrationsUrl(url: string): boolean {
  return url.split('?')[0].endsWith(`/${REGISTRATIONS_URI}`);
}

/**
 * Requests, with how many wait, for the people who decide on them; for those who manage nothing else here, it's all
 * they see.
 */
export function withRegistrationsItem(items: SidebarItem[], content: Pick<Playlist, 'permission' | 'pendingRegistrations'>): SidebarItem[] {
  if (!decidesRegistrations(content)) {
    return items.filter((item) => item.uri !== REGISTRATIONS_URI);
  }
  const counted = items.map((item) =>
    item.uri === REGISTRATIONS_URI ? { ...item, badgeCount: content.pendingRegistrations || undefined } : item,
  );
  return managesContent(content.permission) ? counted : counted.filter((item) => item.uri === REGISTRATIONS_URI);
}
